// MUI-free copy of Common/LogoMarquee for the plain homepage. Same markup, timing and
// seamless-loop maths; the sx styles live in LogoMarquee.module.scss.
import React, {memo, useMemo} from 'react';
import OptimizedImage from '@/components/atoms/OptimizedImage';
import styles from './LogoMarquee.module.scss';

export interface LogoMarqueeItem {
    src: any;
    alt: string;
}

interface LogoMarqueeProps {
    items: LogoMarqueeItem[];
    /** Travel direction of the logos. */
    direction?: 'left' | 'right';
    /** Speed in pixels per second (see Common/LogoMarquee for why it is not a duration). */
    speed?: number;
    /** Cancels the row's 16px vertical padding out of the layout with an equal negative margin. */
    flush?: boolean;
}

const GAP_PX = 24;
/** Per-item horizontal step at the `md` card width — only used to pick the repeat count. */
const ITEM_ADVANCE_PX = 205 + GAP_PX;
/** Widest viewport a single track must cover on its own. */
const MAX_VIEWPORT_PX = 2560;

const trackRepeats = (count: number) => (count > 0 ? Math.max(1, Math.ceil(MAX_VIEWPORT_PX / (count * ITEM_ADVANCE_PX))) : 1);

/** Card heights, exported so ClientServed can reserve the rows' height before they mount. */
export const CARD_HEIGHT = 100;
export const CARD_HEIGHT_COMPACT = 84;

const logoStyle = {width: '100%', height: 'auto', objectFit: 'contain' as const};

const LogoMarquee = ({items, direction = 'left', speed = 26, flush = false}: LogoMarqueeProps) => {
    const trackItems = useMemo(() => {
        const repeats = trackRepeats(items.length);
        return repeats === 1 ? items : Array.from({length: repeats}, () => items).flat();
    }, [items]);

    const duration = (trackItems.length * ITEM_ADVANCE_PX) / speed;

    return (
        <div
            className={flush ? `${styles.viewport} ${styles.flush}` : styles.viewport}
            style={
                {
                    '--marquee-duration': `${duration}s`,
                    '--marquee-direction': direction === 'right' ? 'reverse' : 'normal'
                } as React.CSSProperties
            }
        >
            {[0, 1].map((track) => (
                <div key={track} className={styles.track}>
                    {trackItems.map((item, index) => {
                        // Only the first track's first pass carries the real alt text.
                        const decorative = track !== 0 || index >= items.length;
                        return (
                            <div key={`${track}-${index}`} className={styles.card} aria-hidden={decorative || undefined}>
                                <OptimizedImage src={item.src} alt={decorative ? '' : item.alt} priority={false} style={logoStyle} />
                            </div>
                        );
                    })}
                </div>
            ))}
        </div>
    );
};

export default memo(LogoMarquee);
